import { useEffect, useState } from "react";
import { api, type ExpertSummary } from "../api";
import { ExpertMark } from "../components/design/ExpertMark";

/** Sign-in surface. Split-panel layout: left brand panel showing the
 *  expert lineup + product positioning, right auth panel with three
 *  stacked options (Google Workspace / GitHub / SSO). Mobile collapses
 *  to single column via mobile.css's `.recspec-signin` overrides.
 */
export function Login() {
  const [experts, setExperts] = useState<ExpertSummary[]>([]);

  useEffect(() => {
    // The /v1/experts endpoint is auth-required; on the public login route
    // this will 401. Fall back to a static lineup so the brand panel still
    // shows something representative.
    let alive = true;
    api
      .listExperts()
      .then((list) => {
        if (alive) setExperts(list);
      })
      .catch(() => {
        if (alive) setExperts(STATIC_EXPERT_LINEUP);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      className="recspec-signin"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        // `100vh` is the fallback; `tokens.css` overrides with `100dvh`
        // (dynamic viewport height) for the modern Safari path so the
        // page never leaves a white gap when the address bar resizes.
        minHeight: "100vh",
        background: "var(--paper)",
      }}
    >
      <aside
        style={{
          padding: "56px 48px",
          background: "var(--paper-2)",
          borderRight: "1px solid var(--rule)",
          display: "flex",
          flexDirection: "column",
          gap: 28,
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink)",
              color: "var(--paper)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--mono)",
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            R
          </span>
          <span style={{ fontWeight: 600, fontSize: 18 }}>RecSpec</span>
        </div>

        <h1
          className="serif"
          style={{
            margin: 0,
            fontSize: 40,
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
          }}
        >
          A panel of experts at every meeting.
        </h1>

        <p
          className="dim"
          style={{ margin: 0, fontSize: 15, lineHeight: 1.55, maxWidth: 480 }}
        >
          Pick one or two experts. The bot joins your Google Meet, listens
          through their lens, and at finalize hands you a bundle — designs,
          questions, decisions, and the words that were said.
        </p>

        {experts.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="eyebrow">Today's lineup</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {experts.map((e) => (
                <div
                  key={e.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    background: "var(--card)",
                    border: "1px solid var(--rule)",
                    borderRadius: 8,
                  }}
                >
                  <ExpertMark expert={e} size={28} />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {e.short_name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </aside>

      <section
        style={{
          padding: "56px 48px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          justifyContent: "center",
          maxWidth: 480,
          margin: "0 auto",
          width: "100%",
        }}
      >
        <div>
          <h2
            className="serif"
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 400,
              letterSpacing: "-0.01em",
            }}
          >
            Sign in
          </h2>
          <p className="dim" style={{ marginTop: 8, fontSize: 14 }}>
            Use the account that owns your Google Meet calendar.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            className="btn primary"
            disabled
            title="Coming soon — talk to us about your workspace"
            style={{
              width: "100%",
              justifyContent: "center",
              padding: "12px 16px",
              fontSize: 14,
              opacity: 0.55,
              cursor: "not-allowed",
            }}
          >
            <GoogleMark /> Continue with Google Workspace
          </button>

          <a
            className="btn"
            href="/auth/github/login"
            style={{
              width: "100%",
              justifyContent: "center",
              padding: "12px 16px",
              fontSize: 14,
              background: "var(--ink)",
              color: "var(--paper)",
              borderColor: "var(--ink)",
              textDecoration: "none",
            }}
          >
            <GitHubMark /> Continue with GitHub
          </a>

          <button
            type="button"
            className="btn"
            disabled
            title="Coming soon — talk to us about your workspace"
            style={{
              width: "100%",
              justifyContent: "center",
              padding: "12px 16px",
              fontSize: 14,
              opacity: 0.55,
              cursor: "not-allowed",
            }}
          >
            SSO with your work email
          </button>
        </div>

        <p
          className="dim"
          style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}
        >
          By continuing you acknowledge that meetings RecSpec joins are
          transcribed, frames may be captured during screen-share, and the
          assembled bundle is stored against your account for 7 days.
        </p>
      </section>
    </div>
  );
}

const STATIC_EXPERT_LINEUP: ExpertSummary[] = [
  {
    id: "software-spec-author",
    version: 1,
    display_name: "Software Spec Author",
    short_name: "Spec",
    persona_summary: "OpenSpec proposal · design · tasks",
    default_priority: 0,
    monogram: "SP",
    color_token: "spec",
    voice_id: "warm-baritone",
    voice_desc: "Warm baritone",
    supported_languages: ["EN", "PT"],
    finalize_artifacts: [],
  },
  {
    id: "aws-solutions-architect",
    version: 1,
    display_name: "AWS Solutions Architect",
    short_name: "AWS SA",
    persona_summary: "AWS architecture · cost · WAF",
    default_priority: 10,
    monogram: "AS",
    color_token: "aws",
    voice_id: "crisp-tenor",
    voice_desc: "Crisp tenor",
    supported_languages: ["EN", "PT", "DE"],
    finalize_artifacts: [],
  },
];

function GitHubMark() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2 .37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z"
      />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A10.99 10.99 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
